import { useDataGridContext } from '@cmg/data-grid';
import { SnackbarManager } from '@cmg/design-system';
import { useCallback, useMemo } from 'react';

import { editInvestorContactValidationSchema } from '../FirmsInvestorContactsGrid/components/AddInvestorContact/AddInvestorContact.model';
import {
  DialogMode,
  InvestorContactDialog,
} from '../FirmsInvestorContactsGrid/components/InvestorContactDialog/InvestorContactDialog';
import type { FirmInvestorContactsGridItemFragment } from '../FirmsInvestorContactsGrid/graphql/__generated__';
import type { UpdateInvestorFirmEmployee } from './EditInvestorContactDialog.model';
import { getInitialValues } from './EditInvestorContactDialog.model';
import { useUpdateContact } from './hooks/useUpdateContact';

export type EditInvestorContactDialogProps = Readonly<{
  tenantId?: string;
  firmId: string | null;
  investorEmployeeId: string | null;
  isOpen: boolean;
  initialValues: FirmInvestorContactsGridItemFragment | null;
  onClose: () => void;
}>;

export function EditInvestorContactDialog({
  tenantId,
  firmId,
  investorEmployeeId,
  isOpen,
  initialValues,
  onClose,
}: EditInvestorContactDialogProps) {
  const { refreshServerSide } = useDataGridContext();

  const { updateContact, error } = useUpdateContact(tenantId);

  const handleSubmit = useCallback(
    async (values: UpdateInvestorFirmEmployee) => {
      try {
        if (!firmId || !investorEmployeeId) {
          throw new Error('Missing firmId or investorEmployeeId');
        }

        const { employeeKey, ...updateValues } = values;

        await updateContact({
          employeeKeyOrId: investorEmployeeId,
          firmKeyOrId: firmId,
          input: updateValues,
        });
        refreshServerSide();
        SnackbarManager.success('Investor contact updated successfully.');
        onClose();
      } catch {
        SnackbarManager.error('Failed to update investor contact.');
      }
    },
    [updateContact, firmId, investorEmployeeId, onClose, refreshServerSide]
  );

  const initialFormValues = useMemo(() => getInitialValues(initialValues), [initialValues]);

  if (!initialFormValues) {
    return null;
  }

  return (
    <InvestorContactDialog<UpdateInvestorFirmEmployee>
      mode={DialogMode.Edit}
      open={isOpen}
      initialValues={initialFormValues}
      error={error}
      onClose={onClose}
      onSubmit={handleSubmit}
      validationSchema={editInvestorContactValidationSchema}
    />
  );
}
